// packages import
import { FC } from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  tableCellClasses,
  styled
} from '@mui/material'

// others
import { CaseWithLowTrustBalancePayload } from '../../../../generated'
import { formatCurrency } from '../../../utils'

interface CasesWithLowBalanceTableProps {
  cases: CaseWithLowTrustBalancePayload[] | null
  loading: boolean
  selectedCases: string[]
  onSelectedCasesChange: (selectedCases: string[]) => void
}

const CustomTableRow = styled(TableRow)(({ theme }) => ({
  [`& .${tableCellClasses.head}`]: {
    color: 'white',
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(0.5)
  }
}))

const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  color: 'white',
  '&.Mui-checked': { color: 'white' },
  '&.MuiCheckbox-indeterminate': { color: 'white' }
}))

const CustomTableCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(0),
  paddingBottom: theme.spacing(0),
  border: 'none'
}))

const CasesWithLowBalanceTable: FC<CasesWithLowBalanceTableProps> = ({
  cases,
  loading,
  selectedCases,
  onSelectedCasesChange
}) => {
  const allCaseIds = cases?.map(caseItem => caseItem.id) || []
  const allSelected = allCaseIds.length > 0 && allCaseIds.every(id => selectedCases.includes(id))
  const someSelected = allCaseIds.some(id => selectedCases.includes(id))

  const handleCaseSelect = (caseId: string, checked: boolean) => {
    if (checked) {
      onSelectedCasesChange([...selectedCases, caseId])
    } else {
      onSelectedCasesChange(selectedCases.filter(id => id !== caseId))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      onSelectedCasesChange(allCaseIds)
    } else {
      onSelectedCasesChange([])
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading cases...</Typography>
      </Box>
    )
  }

  if (!cases?.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography fontSize={18} color="text.secondary">
          No matters found with low trust balance
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2, pt: 5 }}>
      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <CustomTableRow>
              <TableCell width="3%">
                <CustomCheckbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={e => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell width="55%">Matters</TableCell>
              <TableCell align="center" width="15%">
                Retainer Amount
              </TableCell>
              <TableCell align="center" width="15%">
                Minimum Threshold
              </TableCell>
              <TableCell align="center" width="15%">
                Current Balance
              </TableCell>
            </CustomTableRow>
          </TableHead>
          <TableBody>
            {cases?.map((caseItem, index) => {
              const { id, caseNumber, title, minimumFundsThreshold, retainerAmount, totalTrustBalance } = caseItem

              return (
                <TableRow key={id}>
                  <CustomTableCell align="left" sx={{ px: 0, pt: index === 0 ? 2 : 0 }}>
                    <Checkbox
                      checked={selectedCases.includes(id)}
                      onChange={e => handleCaseSelect(id, e.target.checked)}
                    />
                  </CustomTableCell>
                  <CustomTableCell align="left" sx={{ pl: 0, pt: index === 0 ? 2 : 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      #{caseNumber} - {title}
                    </Typography>
                  </CustomTableCell>
                  <CustomTableCell align="center" sx={{ pt: index === 0 ? 2 : 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{formatCurrency(retainerAmount)}</Typography>
                  </CustomTableCell>
                  <CustomTableCell align="center" sx={{ pt: index === 0 ? 2 : 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      {formatCurrency(minimumFundsThreshold)}
                    </Typography>
                  </CustomTableCell>
                  <CustomTableCell align="center" sx={{ pt: index === 0 ? 2 : 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                      {formatCurrency(totalTrustBalance)}
                    </Typography>
                  </CustomTableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default CasesWithLowBalanceTable
